class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._primaryCheckoutObserver = null;
    this._stickyResizeObserver = null;
    this._stickyOpenSetupCleanup = null;
    this._onStickyResize = () => this._updateStickyFooterPad();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (e) => {
      if (e.target.id === 'CartDrawer-Overlay' || e.target.closest?.('#CartDrawer-Overlay')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    document.querySelectorAll('header-panel-drawer').forEach(d => {
      if (d.classList.contains('active')) d.close();
    });

    this.querySelectorAll('details[id^="Details-CartDrawer"] > summary').forEach((summary) => {
      if (!summary.hasAttribute('role')) this.setSummaryAccessibility(summary);
    });
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this._scheduleStickyCheckoutAfterOpenAnimation();
    }, 0);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this._clearPendingStickyOpenSetup();
    this.teardownStickyCheckoutObserver();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  /**
   * Re-bind IntersectionObserver after cart section HTML is replaced (e.g. line item updates).
   */
  syncStickyCheckoutObserver() {
    this.teardownStickyCheckoutObserver();
    if (this.classList.contains('active')) {
      requestAnimationFrame(() => this.setupStickyCheckoutObserver());
    }
  }

  _clearPendingStickyOpenSetup() {
    this._stickyOpenSetupCleanup?.();
    this._stickyOpenSetupCleanup = null;
  }

  /**
   * Wait until the drawer panel slide-in (transform) finishes before observing the primary CTA,
   * so the sticky bar does not appear during the open animation.
   */
  _scheduleStickyCheckoutAfterOpenAnimation() {
    this._clearPendingStickyOpenSetup();

    const drawerInner = this.querySelector('#CartDrawer .drawer__inner');
    if (!drawerInner) {
      this.setupStickyCheckoutObserver();
      return;
    }

    let finished = false;
    const onTransitionEnd = (event) => {
      if (event.target !== drawerInner || event.propertyName !== 'transform') return;
      runSetup();
    };
    const runSetup = () => {
      if (finished) return;
      finished = true;
      drawerInner.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallbackTimer);
      this._stickyOpenSetupCleanup = null;
      if (this.classList.contains('active') && !this.classList.contains('is-empty')) {
        this.setupStickyCheckoutObserver();
      }
    };

    drawerInner.addEventListener('transitionend', onTransitionEnd);
    const fallbackTimer = setTimeout(runSetup, 550);

    this._stickyOpenSetupCleanup = () => {
      if (finished) return;
      finished = true;
      drawerInner.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallbackTimer);
      this._stickyOpenSetupCleanup = null;
    };
  }

  setupStickyCheckoutObserver() {
    if (this.classList.contains('is-empty')) return;

    const drawerInner = this.querySelector('#CartDrawer .drawer__inner');
    const primaryCta = this.querySelector('[data-cart-drawer-primary-cta]');
    const stickyFooter = this.querySelector('.drawer__footer-sticky');

    if (!drawerInner || !primaryCta || !stickyFooter) return;

    this._stickyPadInner = drawerInner;
    this._stickyFooterEl = stickyFooter;

    this._primaryCheckoutObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const showSticky = entry && !entry.isIntersecting;
        this.classList.toggle('has-sticky-checkout-visible', showSticky);
        this._updateStickyFooterPad();
      },
      { root: drawerInner, rootMargin: '0px', threshold: 0 }
    );
    this._primaryCheckoutObserver.observe(primaryCta);

    this._stickyResizeObserver = new ResizeObserver(this._onStickyResize);
    this._stickyResizeObserver.observe(stickyFooter);
    window.addEventListener('resize', this._onStickyResize);
  }

  teardownStickyCheckoutObserver() {
    if (this._primaryCheckoutObserver) {
      this._primaryCheckoutObserver.disconnect();
      this._primaryCheckoutObserver = null;
    }
    if (this._stickyResizeObserver) {
      this._stickyResizeObserver.disconnect();
      this._stickyResizeObserver = null;
    }
    window.removeEventListener('resize', this._onStickyResize);
    this.classList.remove('has-sticky-checkout-visible');
    const inner = this.querySelector('#CartDrawer .drawer__inner');
    if (inner) inner.style.paddingBottom = '';
    this._stickyPadInner = null;
    this._stickyFooterEl = null;
  }

  _updateStickyFooterPad() {
    const inner = this._stickyPadInner || this.querySelector('#CartDrawer .drawer__inner');
    const sticky = this._stickyFooterEl || this.querySelector('.drawer__footer-sticky');
    if (!inner || !sticky) return;
    if (this.classList.contains('has-sticky-checkout-visible')) {
      inner.style.paddingBottom = `${sticky.offsetHeight}px`;
    } else {
      inner.style.paddingBottom = '';
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  updateContents(parsedState) {
    // Remove is-empty class from cart-drawer element if present
    if (this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
    }

    // Remove drawer__inner-empty element if it exists
    const emptyContent = this.querySelector('.drawer__inner-empty');
    if (emptyContent) {
      emptyContent.remove();
    }

    // Update sections without opening the drawer
    if (parsedState && parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        if (parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      });
    }

    // Update productId if available
    if (parsedState && parsedState.id) {
      this.productId = parsedState.id;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  disconnectedCallback() {
    this._clearPendingStickyOpenSetup();
    this.teardownStickyCheckoutObserver();
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
